import { useEffect } from "react";

const LAYER_ID = "eh-halloween-layer";
const BAT_COUNT = 14;
const FOG_COUNT = 4;
const HALLOWEEN_MOTION_VERSION = "3";

function queryPrimaryMainEl(): Element | null {
  return (
    document.querySelector("main.eh-layout-main") ||
    document.querySelector("main#main") ||
    document.querySelector("main.main-area") ||
    document.querySelector("main:not([hidden])")
  );
}

const BAT_SIZES = ["s", "m", "l"] as const;

interface FlightPoint {
  x: number; // vw
  y: number; // vh
}

const FLIGHT_Y_MIN = 3;
const FLIGHT_Y_MAX = 84;
const SPLINE_SAMPLES_PER_SEGMENT = 8;
const HEADING_LOOKAHEAD = 3;
const MAX_TURN_PER_SAMPLE = 22;

/** Statt am Rand „kleben zu bleiben“ prallt die Höhe zurück ins Bild. */
function reflectY(value: number): number {
  let y = value;
  if (y < FLIGHT_Y_MIN) y = FLIGHT_Y_MIN + (FLIGHT_Y_MIN - y);
  if (y > FLIGHT_Y_MAX) y = FLIGHT_Y_MAX - (y - FLIGHT_Y_MAX);
  return Math.min(FLIGHT_Y_MAX, Math.max(FLIGHT_Y_MIN, y));
}

function catmullRom(p0: FlightPoint, p1: FlightPoint, p2: FlightPoint, p3: FlightPoint, t: number): FlightPoint {
  const t2 = t * t;
  const t3 = t2 * t;
  const axis = (a: number, b: number, c: number, d: number) =>
    0.5 * (2 * b + (-a + c) * t + (2 * a - 5 * b + 4 * c - d) * t2 + (-a + 3 * b - 3 * c + d) * t3);
  return { x: axis(p0.x, p1.x, p2.x, p3.x), y: axis(p0.y, p1.y, p2.y, p3.y) };
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/**
 * Zufällige, unruhige Route quer über den Viewport: überwiegend vorwärts,
 * mit Höhenwechseln, Sturzflügen und gelegentlichem Zurückziehen.
 */
function buildFlightPath(): FlightPoint[] {
  const leftToRight = Math.random() > 0.5;
  const segments = 8 + Math.floor(Math.random() * 5);
  const span = 124; // -12vw … 112vw
  const dir = leftToRight ? 1 : -1;
  const startX = leftToRight ? -12 : 112;

  const points: FlightPoint[] = [];
  let y = 8 + Math.random() * 64;
  let x = startX;
  points.push({ x, y });

  for (let i = 1; i < segments; i += 1) {
    const progress = i / segments;
    const step = (span / segments) * (0.55 + Math.random() * 0.9);
    x += dir * step;

    const roll = Math.random();
    if (roll < 0.22) {
      // Sturzflug / Steigflug.
      y = reflectY(y + (Math.random() < 0.5 ? -1 : 1) * (22 + Math.random() * 28));
    } else if (roll < 0.34) {
      // Kurzer Haken gegen die Hauptrichtung, dann weiter.
      x -= dir * (6 + Math.random() * 14);
      y = reflectY(y + (Math.random() - 0.5) * 26);
    } else {
      y = reflectY(y + (Math.random() - 0.5) * 36);
    }

    // Nicht zu weit vor dem Zielrand enden – sonst wirkt die Bahn gestutzt.
    const aheadLimit = startX + dir * span * (progress + 0.08);
    if (dir > 0) x = Math.min(x, aheadLimit);
    else x = Math.max(x, aheadLimit);

    points.push({ x, y });
  }

  points.push({ x: leftToRight ? 112 : -12, y: reflectY(y + (Math.random() - 0.5) * 20) });
  return points;
}

/**
 * Wegpunkte → weiche Kurve (Catmull-Rom). Liefert dichte Stützpunkte samt
 * Zeitanteil; die Segmentdauern bleiben ungleich (schnelle Stöße, kurze Gleitphasen).
 */
function sampleFlightCurve(points: FlightPoint[]): { point: FlightPoint; offset: number }[] {
  const segmentCount = points.length - 1;
  const weights = Array.from({ length: segmentCount }, () => 0.55 + Math.random() * 1.3);
  const total = weights.reduce((sum, w) => sum + w, 0);

  const samples: { point: FlightPoint; offset: number }[] = [];
  let elapsed = 0;
  for (let i = 0; i < segmentCount; i += 1) {
    const p0 = points[Math.max(i - 1, 0)]!;
    const p1 = points[i]!;
    const p2 = points[i + 1]!;
    const p3 = points[Math.min(i + 2, points.length - 1)]!;
    const segmentShare = weights[i]! / total;
    for (let k = 0; k < SPLINE_SAMPLES_PER_SEGMENT; k += 1) {
      const t = k / SPLINE_SAMPLES_PER_SEGMENT;
      samples.push({ point: catmullRom(p0, p1, p2, p3, t), offset: elapsed + segmentShare * t });
    }
    elapsed += segmentShare;
  }
  samples.push({ point: points[points.length - 1]!, offset: 1 });
  return samples;
}

/** Kopf zeigt im SVG nach oben → Kurs = atan2 + 90°. Kurven werden begrenzt mitgeschwenkt. */
function headingsForPath(points: FlightPoint[]): number[] {
  const headings: number[] = [];
  let previous: number | null = null;
  for (let i = 0; i < points.length; i += 1) {
    const from = points[i]!;
    const to = points[Math.min(i + HEADING_LOOKAHEAD, points.length - 1)]!;
    let angle: number;
    if (i >= points.length - 1 && previous !== null) {
      angle = previous;
    } else {
      const dx = ((to.x - from.x) * window.innerWidth) / 100;
      const dy = ((to.y - from.y) * window.innerHeight) / 100;
      if (Math.abs(dx) + Math.abs(dy) < 2 && previous !== null) {
        angle = previous;
      } else {
        angle = (Math.atan2(dy, dx) * 180) / Math.PI + 90;
      }
      if (previous !== null) {
        let delta = angle - previous;
        while (delta > 180) delta -= 360;
        while (delta < -180) delta += 360;
        if (delta > MAX_TURN_PER_SAMPLE) delta = MAX_TURN_PER_SAMPLE;
        if (delta < -MAX_TURN_PER_SAMPLE) delta = -MAX_TURN_PER_SAMPLE;
        angle = previous + delta;
      }
    }
    headings.push(angle);
    previous = angle;
  }
  return headings;
}

function edgeOpacity(xVw: number): number {
  if (xVw < -2) return 0;
  if (xVw < 6) return (xVw + 2) / 8;
  if (xVw > 102) return 0;
  if (xVw > 94) return (102 - xVw) / 8;
  return 1;
}

function buildFlightKeyframes(points: FlightPoint[], baseOpacity: number): Keyframe[] {
  const samples = sampleFlightCurve(points);
  const headings = headingsForPath(samples.map((s) => s.point));
  return samples.map((s, i) => ({
    offset: Math.min(1, Math.max(0, s.offset)),
    transform: `translate(${s.point.x.toFixed(2)}vw, ${s.point.y.toFixed(2)}vh) rotate(${headings[i]!.toFixed(1)}deg)`,
    opacity: edgeOpacity(s.point.x) * baseOpacity,
    easing: "linear",
  }));
}

const BAT_BASE_OPACITY: Record<(typeof BAT_SIZES)[number], number> = {
  s: 0.55,
  m: 0.7,
  l: 0.82,
};

function launchBat(bat: HTMLElement, index: number, scatterAlongPath = false): void {
  bat.getAnimations().forEach((anim) => anim.cancel());

  const size = BAT_SIZES[index % BAT_SIZES.length] ?? "m";
  const sizeFactor = size === "l" ? 1.15 : size === "s" ? 0.85 : 1;
  const baseOpacity = BAT_BASE_OPACITY[size];
  bat.style.setProperty("--eh-bat-flap", `${0.28 + Math.random() * 0.2}s`);

  const points = buildFlightPath();
  const keyframes = buildFlightKeyframes(points, baseOpacity);

  if (prefersReducedMotion() || typeof bat.animate !== "function") {
    const inner = keyframes.slice(SPLINE_SAMPLES_PER_SEGMENT, -SPLINE_SAMPLES_PER_SEGMENT);
    const pick = inner[Math.floor(Math.random() * inner.length)] ?? keyframes[0];
    bat.style.transform = String(pick?.transform ?? "");
    bat.style.opacity = String(baseOpacity);
    return;
  }

  const duration = (14000 + Math.random() * 14000) * sizeFactor;
  const anim = bat.animate(keyframes, {
    duration,
    iterations: 1,
    fill: "forwards",
  });
  if (scatterAlongPath) {
    anim.currentTime = Math.random() * duration * 0.82;
  }
  anim.onfinish = () => {
    if (!bat.isConnected) return;
    launchBat(bat, index, false);
  };
}

function buildHalloweenInner(): HTMLDivElement {
  const inner = document.createElement("div");
  inner.className = "eh-global-halloween";
  inner.dataset.motion = HALLOWEEN_MOTION_VERSION;

  for (let i = 0; i < FOG_COUNT; i += 1) {
    const fog = document.createElement("span");
    fog.className = "eh-halloween-fog";
    fog.style.left = `${-10 + (i * 110) / FOG_COUNT + Math.random() * 8}%`;
    fog.style.setProperty("--eh-fog-size", `${34 + Math.random() * 22}vw`);
    fog.style.setProperty(
      "--eh-fog-drift",
      `${(30 + Math.random() * 60) * (Math.random() > 0.5 ? 1 : -1)}px`,
    );
    fog.style.animationDuration = `${26 + Math.random() * 18}s`;
    fog.style.animationDelay = `${-Math.random() * 30}s`;
    inner.appendChild(fog);
  }

  for (let i = 0; i < BAT_COUNT; i += 1) {
    const bat = document.createElement("span");
    bat.className = `eh-bat eh-bat--${BAT_SIZES[i % BAT_SIZES.length]}`;
    inner.appendChild(bat);
  }
  return inner;
}

/** Fledermäuse + Bodennebel und optionale Halloween-Dekoration (24.–31. Oktober / Dev-Simulation). */
export function useHalloweenEffects(
  seasonActive: boolean,
  options: { showBats?: boolean; showSeasonDecor?: boolean } = {},
): void {
  const showBats = options.showBats ?? seasonActive;
  const showSeasonDecor = options.showSeasonDecor ?? seasonActive;

  useEffect(() => {
    if (typeof document === "undefined") return;

    let clipRaf = 0;
    let resizeObserver: ResizeObserver | null = null;

    const disconnectObserver = () => {
      resizeObserver?.disconnect();
      resizeObserver = null;
    };

    const updateClip = () => {
      const layer = document.getElementById(LAYER_ID);
      if (!layer || layer.hasAttribute("hidden")) return;

      const main = queryPrimaryMainEl();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      if (!main) {
        layer.style.clipPath = "none";
        return;
      }

      const rect = main.getBoundingClientRect();
      if (rect.width < 4 || rect.height < 4) {
        layer.style.clipPath = "none";
        return;
      }

      const pad = 6;
      const left = Math.max(0, rect.left - pad);
      const top = Math.max(0, rect.top - pad);
      const right = Math.min(vw, rect.right + pad);
      const bottom = Math.min(vh, rect.bottom + pad);

      layer.style.clipPath = [
        "polygon(evenodd,",
        `0px 0px, ${vw}px 0px, ${vw}px ${vh}px, 0px ${vh}px, 0px 0px,`,
        `${left}px ${top}px, ${right}px ${top}px, ${right}px ${bottom}px, ${left}px ${bottom}px, ${left}px ${top}px)`,
      ].join(" ");
    };

    const scheduleClip = () => {
      if (clipRaf) cancelAnimationFrame(clipRaf);
      clipRaf = requestAnimationFrame(() => {
        clipRaf = 0;
        updateClip();
      });
    };

    const bindMainObserver = () => {
      disconnectObserver();
      const main = queryPrimaryMainEl();
      if (!main) return;
      resizeObserver = new ResizeObserver(() => scheduleClip());
      resizeObserver.observe(main);
    };

    const teardownBats = () => {
      document.body.classList.remove("eh-halloween-bats");
      const layer = document.getElementById(LAYER_ID);
      layer?.querySelectorAll(".eh-bat").forEach((node) => {
        (node as HTMLElement).getAnimations().forEach((anim) => anim.cancel());
      });
      layer?.setAttribute("hidden", "");
      layer?.style.removeProperty("clip-path");
      disconnectObserver();
      window.removeEventListener("resize", scheduleClip);
      window.removeEventListener("scroll", scheduleClip, true);
      if (clipRaf) cancelAnimationFrame(clipRaf);
    };

    const teardownAll = () => {
      document.body.classList.remove("eh-halloween-season", "eh-halloween-bats");
      teardownBats();
    };

    if (!seasonActive) {
      teardownAll();
      return teardownAll;
    }

    document.body.classList.toggle("eh-halloween-season", showSeasonDecor);

    if (!showBats) {
      teardownBats();
      return () => {
        document.body.classList.remove("eh-halloween-season", "eh-halloween-bats");
      };
    }

    document.body.classList.add("eh-halloween-bats");

    let layer = document.getElementById(LAYER_ID);
    if (!layer) {
      layer = document.createElement("div");
      layer.id = LAYER_ID;
      layer.setAttribute("aria-hidden", "true");
      layer.appendChild(buildHalloweenInner());
      const anchor = document.body.firstElementChild;
      if (anchor) document.body.insertBefore(layer, anchor);
      else document.body.appendChild(layer);
    } else {
      const inner = layer.querySelector(".eh-global-halloween") as HTMLElement | null;
      if (!inner || inner.dataset.motion !== HALLOWEEN_MOTION_VERSION) {
        inner?.remove();
        layer.appendChild(buildHalloweenInner());
      }
    }

    layer.removeAttribute("hidden");
    layer.querySelectorAll(".eh-bat").forEach((node, i) => {
      launchBat(node as HTMLElement, i, true);
    });
    bindMainObserver();
    scheduleClip();

    window.addEventListener("resize", scheduleClip, { passive: true });
    window.addEventListener("scroll", scheduleClip, { passive: true, capture: true });

    return () => {
      document.body.classList.remove("eh-halloween-season", "eh-halloween-bats");
      teardownBats();
    };
  }, [seasonActive, showBats, showSeasonDecor]);
}
